import React, { useState, useEffect, Suspense } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import * as mui from '@mui/material';
import { keyframes } from '@mui/material/styles';
import ResponsiveContainer from '../../layout/Container';
import userService from '../../../services/UserService';
import appService from '../../../services/ApplicationService';
import companyService from '../../../services/CompanyService';
import ErrorBoundary from '../../common/ErrorBoundery';
import LogoLoader from '../../common/LogoLoader';
import {
    Dashboard as DashboardIcon,
    GridView as GridViewIcon,
    AssuredWorkload as AssuredWorkloadIcon,
    AccountTree as AccountTreeIcon,
    Badge as BadgeIcon,
    MarkEmailUnread as MarkEmailUnreadIcon,
    RateReview as RateReviewIcon,
    Inventory as InventoryIcon,
    Summarize as SummarizeIcon
} from '@mui/icons-material';

const globalStyles = `
    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
    }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes blink {
        0% { opacity: 0.6; }
        50% { opacity: 1; }
        100% { opacity: 0.6; }
    }
    .premium-card {
        position: relative;
        isolation: isolate;
        overflow: hidden;
    }
    .premium-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #42a5f5, #1976d2);
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 1;
    }
    .premium-card:hover::before {
        opacity: 1;
    }
`;

const SysOwnerDashboard = () => {
    const [users, setUsers] = useState([]);
    const [apps, setApps] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Define a spin animation for the loader
    const spin = keyframes`
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    `;

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = globalStyles;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                
                // Add a 2-second delay before fetching data
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                const [appsResponse, usersResponse, currentUserresponse, companyResponse] = await Promise.all([
                    appService.fetchApps(),
                    userService.fetchUsers(),
                    userService.fetchCurrentUser(),
                    companyService.fetchCompanies()
                ]);

                setApps(appsResponse);
                setUsers(usersResponse);
                setCurrentUser(currentUserresponse);
                setCompanies(companyResponse);
            } catch (error) {
                console.error(`Error fetching data: ${error.message}`);
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    const menuItems = [
        {
            icon: <GridViewIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: "My Applications",
            description: "View and manage your applications and associated controls",
            to: "/applications",
            buttonLabel: "View",
            isComingSoon: false
        },
        {
            icon: <AssuredWorkloadIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: "Compliance Status",
            description: "Monitor compliance status across your applications",
            to: "/compliance",
            buttonLabel: "View",
            isComingSoon: false
        },
        {
            icon: <AccountTreeIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: "Process Narrative",
            description: "Document and review process flows and narratives",
            to: "/processnarrative",
            buttonLabel: "View",
            isComingSoon: false
        },
        {
            icon: <BadgeIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: "New Access Requests",
            description: "Review and respond to new access requests",
            to: "/myrequests",
            buttonLabel: "View",
            isComingSoon: false
        },
        {
            icon: <MarkEmailUnreadIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: "Process Requests",
            description: "Process pending access and permission requests",
            to: "/accessrequest/granting",
            buttonLabel: "View",
            isComingSoon: false
        },
        {
            icon: <RateReviewIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: "User Access Review",
            description: "Conduct and manage user access reviews",
            to: "/useraccessreview",
            buttonLabel: "View",
            isComingSoon: false
        },
        {
            icon: <InventoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: "Audit Requests",
            description: "Respond to and manage audit requests and findings",
            to: "/security",
            buttonLabel: "View",
            isComingSoon: false
        },
        {
            icon: <SummarizeIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: "Reports",
            description: "Access and generate compliance and audit reports",
            to: "/Interfaces",
            buttonLabel: "View",
            isComingSoon: false
        }
    ];

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Typography color="text.primary">My Dashboard</mui.Typography>
                        <mui.Typography color="text.primary">System Owner</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <DashboardIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                {currentUser ? `Welcome ${currentUser.first_name} ${currentUser.last_name}` : 'Welcome'}
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Manage Access Requests and Approvals, PBC Requests, and Compliance Status
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 3,
                        minHeight: '80vh',
                        boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                    }}>
                        <mui.Paper 
                            elevation={0}
                            sx={{ 
                                p: 2,
                                height: '100%',
                                backgroundColor: 'transparent'
                            }}
                        >
                            <mui.Grid container spacing={2.5}>
                                {menuItems.map((item, index) => (
                                    <mui.Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                        <mui.Paper
                                            component={mui.Link}
                                            href={item.to}
                                            className="premium-card"
                                            sx={{
                                                p: 3,
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderRadius: 2,
                                                backgroundImage: 'linear-gradient(to bottom, #ffffff, #fafbfd)',
                                                textDecoration: 'none',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                transition: 'all 0.25s ease',
                                                boxShadow: 'rgba(0, 0, 0, 0.03) 0px 2px 8px, rgba(0, 0, 0, 0.01) 0px 0px 0px 1px',
                                                animation: 'fadeIn 0.5s ease-out',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    inset: 0,
                                                    zIndex: -1,
                                                    opacity: 0,
                                                    transition: 'opacity 0.3s ease',
                                                    background: 'linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(240,249,255,0.2) 100%)',
                                                },
                                                ...(item.isComingSoon && {
                                                    opacity: 0.85,
                                                }),
                                                '&:hover': {
                                                    transform: 'translateY(-3px)',
                                                    boxShadow: 'rgba(17, 12, 46, 0.08) 0px 8px 25px 0px',
                                                    backgroundImage: 'linear-gradient(to bottom, #ffffff, #f5f9ff)',
                                                    '& .icon': {
                                                        color: 'primary.main',
                                                    },
                                                    '& .title': {
                                                        color: 'primary.main',
                                                    },
                                                    '&::after': {
                                                        opacity: 1,
                                                    },
                                                    ...(item.isComingSoon && {
                                                        opacity: 0.95,
                                                    }),
                                                }
                                            }}
                                        >
                                            {item.isComingSoon && (
                                                <mui.Tooltip
                                                    title="Coming Soon"
                                                    placement="top"
                                                    arrow
                                                    sx={{
                                                        '& .MuiTooltip-tooltip': {
                                                            fontSize: '0.7rem',
                                                            padding: '4px 8px',
                                                            backgroundColor: 'rgba(97, 97, 97, 0.9)',
                                                        }
                                                    }}
                                                >
                                                    <mui.Box 
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 10,
                                                            right: 10,
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: '50%',
                                                            backgroundColor: 'primary.main',
                                                            opacity: 0.7,
                                                            cursor: 'help'
                                                        }}
                                                    />
                                                </mui.Tooltip>
                                            )}
                                            <mui.Box 
                                                sx={{ 
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    mb: 2
                                                }}
                                            >
                                                {React.cloneElement(item.icon, { 
                                                    sx: { 
                                                        fontSize: 24,
                                                        color: 'text.secondary',
                                                        transition: 'color 0.2s ease-in-out'
                                                    },
                                                    className: 'icon'
                                                })}
                                                <mui.Box sx={{ ml: 1.5 }}>
                                                    <mui.Typography 
                                                        variant="subtitle1" 
                                                        className="title"
                                                        sx={{ 
                                                            fontWeight: 600,
                                                            color: 'text.primary',
                                                            fontSize: '0.95rem',
                                                            mb: 0.75,
                                                            lineHeight: 1.3,
                                                            transition: 'color 0.2s ease-in-out'
                                                        }}
                                                    >
                                                        {item.title}
                                                    </mui.Typography>
                                                    <mui.Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            fontSize: '0.8rem',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        {item.description}
                                                    </mui.Typography>
                                                </mui.Box>
                                            </mui.Box>

                                            <mui.Box 
                                                sx={{ 
                                                    mt: 'auto',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: 'primary.main',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    paddingTop: 1.5,
                                                    borderTop: '1px solid',
                                                    borderColor: 'rgba(0, 0, 0, 0.04)',
                                                    '& .arrow': {
                                                        transition: 'transform 0.2s ease-in-out'
                                                    },
                                                    '.MuiPaper-root:hover & .arrow': {
                                                        transform: 'translateX(4px)'
                                                    }
                                                }}
                                            >
                                                {item.buttonLabel}
                                                <mui.Box 
                                                    component="span"
                                                    className="arrow"
                                                    sx={{ 
                                                        ml: 0.75,
                                                        display: 'inline-block'
                                                    }}
                                                >
                                                    →
                                                </mui.Box>
                                            </mui.Box>
                                        </mui.Paper>
                                    </mui.Grid>
                                ))}
                            </mui.Grid>
                        </mui.Paper>
                    </mui.Box>
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    // Show loader if still loading
    if (isLoading) {
        console.log('SysOwnerDashboard: Showing LogoLoader');
        return (
            <mui.Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    backgroundColor: '#f8fafc'
                }}
            >
                <LogoLoader 
                    size={80} 
                    message="Loading Dashboard..." 
                    duration={1.5} 
                />
            </mui.Box>
        );
    }

    console.log('SysOwnerDashboard: Loading complete, showing dashboard');
    
    return (
        <ErrorBoundary fallback={<p>Failed to load data.</p>} error={error}>
            <div>
                <SysOwnerSideBar mainContent={customMainContent} />
            </div>
        </ErrorBoundary>
    );
};

export default SysOwnerDashboard;
